import { Alert, AlertDescription, AlertTitle, cn } from '@kalos/ui';
import { useEffect, useRef, useState } from 'react';

export type FormAlert = {
  variant: 'destructive' | 'warning';
  title: string;
  message: string;
};

const HIGHLIGHT_MS = 700;

/**
 * Dedicated full-width slot for form-level alerts. Renders nothing when there is no alert.
 * Every time an alert is set (even the same content again) it scrolls into view, replays its
 * enter animation, and flashes a ring, so repeated submits always give visible feedback.
 */
export const FormAlertArea = ({
  alert,
  className,
}: {
  alert: FormAlert | null;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [flashKey, setFlashKey] = useState(0);
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    if (!alert) return;
    ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    setFlashKey((key) => key + 1);
    setHighlighted(true);
    const timer = window.setTimeout(() => setHighlighted(false), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [alert]);

  if (!alert) return null;
  return (
    <div ref={ref} className={cn('col-span-full', className)}>
      <Alert
        key={flashKey}
        variant={alert.variant}
        className={cn(
          'animate-in fade-in-0 zoom-in-95 transition-shadow duration-300',
          highlighted && 'ring-2 ring-offset-2',
          highlighted && alert.variant === 'destructive' && 'ring-destructive',
          highlighted && alert.variant === 'warning' && 'ring-yellow-500',
        )}
      >
        <AlertTitle>{alert.title}</AlertTitle>
        <AlertDescription>{alert.message}</AlertDescription>
      </Alert>
    </div>
  );
};
